import { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import moment from 'moment';

// MUI
import { Button, Paper } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

// Services
import { getCompanies } from '../../../services/companies.services';

// Utils
import { makeCompaniesColumn } from '../../../utils/columns/makeCompaniesColumn';
import { shortDateFormat } from '../../../utils/Format';
import { useExport } from '../../../hooks/useExport';
import { Storage } from '../../../utils/Storage';

// Types
import { Company } from '../../../types/Company';
import { AppPermissions } from '../../../constants/Permissions';
import { SearchBarFilterTypes } from '../../../constants/Constants';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import { AuthGuard } from '../../common/AuthGuard';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';

import CompaniesRelationView from './CompaniesRelationView';
import AddOrUpdateCompany from './AddOrUpdateCompany';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const Companies = () => {
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'Company',
    filters: [],
  });
  const [isResetting, setIsResetting] = useState(false);
  const [companyRelationsPopupOpen, setCompanyRelationsPopupOpen] = useState(false);
  const [companyDetailsPopUp, setCompanyDetailsPopUp] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string | null>(null);

  const [companyFormSubmit, setCompanyFormSubmit] = useState<(() => void) | null>(null);
  const [companyFormValid, setCompanyFormValid] = useState<boolean>(false);

  const { isExporting, startExport } = useExport('Company');

  const handleEditCompanyDetails = (id: string, carrierId?: string | null) => {
    setSelectedCompanyId(id);
    setSelectedCarrierId(carrierId ?? null);
    setCompanyDetailsPopUp(true);
  };

  const handleOpenRelationsPopup = (id: string) => {
    setSelectedCompanyId(id);
    setCompanyRelationsPopupOpen(true);
  };

  const columns = useMemo(() => makeCompaniesColumn(handleEditCompanyDetails, handleOpenRelationsPopup), []);

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ entityType: 'Company', filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  // Restore filters on mount
  useEffect(() => {
    const storedFilter = Storage.getSearchBarFiltering(SearchBarFilterTypes.COMPANIES);
    if (storedFilter?.filters?.length) {
      setParamFilter({ entityType: 'Company', filters: storedFilter.filters });
    }
  }, []);

  // Handle reset
  const handleReset = () => {
    setIsResetting(true);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
    setParamFilter({ entityType: 'Company', filters: [], sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.COMPANIES, { filters: [] });
  };

  // Handle search change
  const onSearchChange = (filters: any) => {
    const updatedFilters = filters.map((f: any) => ({
      ...f,
      filterType: FilterableTypes.Contains,
    }));

    setParamFilter({ entityType: 'Company', filters: updatedFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.COMPANIES, { filters: updatedFilters });
  };

  const table = useTable<Company>({
    columns,
    fetchAsync: getCompanies,
    params: paramFilter,
    serverSide: true,
  });

  return (
    <AuthGuard permission={AppPermissions.Company.Read}>
      <div style={{ height: '30px', position: 'relative' }}>
        <div style={{ position: 'absolute', top: -14, right: 0 }}>
          <Button
            onClick={() => {
              setSelectedCompanyId(null);
              setSelectedCarrierId(null);
              setCompanyDetailsPopUp(true);
            }}
            variant='contained'
            size='small'
            sx={tablePrimaryButtonSx}
            endIcon={<AddCircleOutlineOutlinedIcon style={{ fontSize: 20 }} />}
          >
            Add Company
          </Button>
        </div>
      </div>
      <SearchBar
        pageKey={SearchBarFilterTypes.COMPANIES}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormInputCollection label={'Created Date'} style={{ marginBottom: '8px' }}>
              <div style={{ marginRight: '8px' }}>
                <DatePicker
                  label='From'
                  value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => setLastSeenDateTimeFrom(date ? date.toDate() : null)}
                />
              </div>
              <DatePicker
                value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                label='To'
                format={shortDateFormat}
                sx={{ width: '185px' }}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(date) => setLastSeenDateTimeTo(date ? date.toDate() : null)}
              />
            </FormInputCollection>
          </LocalizationProvider>
        }
      />
      <Paper>
        <Table
          {...table}
          isExporting={isExporting}
          canExportData={() => true}
          exportData={() => startExport(paramFilter.filters ?? [])}
          showSearchBar={false}
          showColumnToggler={true}
          getCoreRowModel={getCoreRowModel()}
        />
      </Paper>
      <CustomModal isOpen={companyRelationsPopupOpen} setIsOpen={setCompanyRelationsPopupOpen} showSaveButton={false} maxWidth='xl'>
        {selectedCompanyId && <CompaniesRelationView id={selectedCompanyId} />}
      </CustomModal>
      <CustomModal
        title='Update Company Details'
        isOpen={companyDetailsPopUp}
        setIsOpen={setCompanyDetailsPopUp}
        showSaveButton={false}
        maxWidth='md'
      >
        {companyDetailsPopUp && (
          <CustomModal
            title={selectedCompanyId ? 'Update Company Details' : 'Add Company'}
            isOpen={companyDetailsPopUp}
            setIsOpen={(open) => {
              setCompanyDetailsPopUp(open);
              if (!open) {
                // reset form callbacks when modal closes
                setCompanyFormSubmit(null);
                setCompanyFormValid(false);
              }
            }}
            onConfirm={() => {
              if (companyFormSubmit) companyFormSubmit();
            }}
            isValid={companyFormValid}
            maxWidth='md'
          >
            <AddOrUpdateCompany
              companyId={selectedCompanyId ?? undefined}
              initialCarrierId={selectedCompanyId ? (selectedCarrierId ?? undefined) : undefined}
              onSaved={() => {
                setCompanyDetailsPopUp(false);
                // trigger table refresh
                setParamFilter((prev: any) => ({ ...prev, changedAt: new Date() }));
                // cleanup
                setCompanyFormSubmit(null);
                setCompanyFormValid(false);
                setSelectedCompanyId(null);
                setSelectedCarrierId(null);
              }}
              onCancel={() => setCompanyDetailsPopUp(false)}
              onRegisterSubmit={(submitFn) => setCompanyFormSubmit(() => submitFn)}
              onFormChange={(isValid, isSubmitting) => {
                setCompanyFormValid(Boolean(isValid && !isSubmitting));
              }}
            />
          </CustomModal>
        )}
      </CustomModal>
    </AuthGuard>
  );
};

export default Companies;
